import { formatDate } from '~/app/utils/format-data';
import { Text } from '~/ui/index.js';
import {
  Chip,
  Dialog,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import s from './styles.module.scss';
import { useGetListVersionFilesMetadata, useGetVersions } from '~/data/reports/queries';
import { useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useDownloadVisualExcel } from '~/app/components/pages/auditor/clients/client/report/report/hooks';
import { z } from 'zod';
import cx from '~/data/utils/helpers/cx';
import { match } from 'ts-pattern';
import { Show } from '~/app/components/core/show';
import { FileProcessingInfo } from '~/app/components/pages/auditor/clients/client/report/report/FileProcessingInfo';
import type { BftStatus, TaxonomyMetadataRepresentation, VisualExcelStatus } from '~/data/openapi-client/index';
import { toast } from 'react-toastify';

interface UploadedFilesProps {
  versionId: string;
  open: boolean;
  onClose: () => void;
}

const VersionFileId = z.string().brand('VersionFileId');
type VersionFileId = z.infer<typeof VersionFileId>;

const BftStatusName: Record<BftStatus, string> = {
  NotYetStarted: 'Pending',
  Processing: 'Processing',
  Done: 'Done',
  Failed: 'Failed',
  Saving: '',
};

const VisualExcelStatusName: Record<VisualExcelStatus, string> = {
  Pending: 'Pending',
  Succeeded: 'Done',
  Failed: 'Failed',
};

const UploadedFilesDialog = ({ versionId, open, onClose }: UploadedFilesProps) => {
  const { clientId, reportId } = useParams<{ clientId: string; reportId: string }>();
  const { data: version } = useGetVersions(clientId, reportId, versionId);
  const { data: versionFilesMetadata } = useGetListVersionFilesMetadata(clientId, reportId, versionId);

  const { files } = version ?? {};

  const { mutate: downloadVisualExcel } = useDownloadVisualExcel();

  const onDownloadExcel = (versionFileId: VersionFileId) => {
    const file = versionFilesMetadata?.find((f) => f.id === versionFileId);
    if (!file) throw new Error(`Internal Error. No version file is found for ${versionFileId}`);

    downloadVisualExcel({ resourceURI: file.visualExcelDownloadURI, filename: file.visualExcelFilename });
  };

  const copyTaxonomyMetadata = (taxonomy: TaxonomyMetadataRepresentation) => {
    const text = `container version: ${taxonomy.containerVersion}; validation version: ${taxonomy.validationVersion}; package guid: ${taxonomy.packageGuid}; particles version: ${taxonomy.particlesVersion}`;

    navigator.clipboard.writeText(text).then(() => toast.success(`Copied ${taxonomy.name}`));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle sx={{ m: 0, p: 0 }} className="flex justify-between items-center">
        <Typography fontSize={20}>Uploaded files</Typography>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </DialogTitle>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Text weight={Text.weight.semiMedium} size={Text.size.s}>
                  File
                </Text>
              </TableCell>
              <TableCell align="right">
                <Text weight={Text.weight.semiMedium} size={Text.size.s}>
                  Date
                </Text>
              </TableCell>
              <TableCell align="right">
                <Text weight={Text.weight.semiMedium} size={Text.size.s}>
                  Taxonomy
                </Text>
              </TableCell>
              <TableCell align="right">
                <Text weight={Text.weight.semiMedium} size={Text.size.s}>
                  BFT
                </Text>
              </TableCell>
              <TableCell align="right">
                <Text weight={Text.weight.semiMedium} size={Text.size.s}>
                  Visual Excel
                </Text>
              </TableCell>
            </TableRow>
          </TableHead>
          {!!files?.length && (
            <TableBody>
              {files.map((file) => {
                const metadata = versionFilesMetadata?.find((f) => f.id === file.id);
                return (
                  <TableRow>
                    <TableCell className={s.filesTableFileCol}>
                      <Text size={Text.size.s}>{file.name}</Text>
                      {metadata?.xbrlFormat === 'Csv' && (
                        <Text size={Text.size.xs} color={Text.color.light}>
                          xBRL-CSV
                        </Text>
                      )}
                    </TableCell>
                    <TableCell width="10%" align="right">
                      <Text size={Text.size.s}>{formatDate(file.inserted)}</Text>
                    </TableCell>
                    <TableCell className={s.filesTableFileCol} width="20%" align="right">
                      <Text size={Text.size.xs} color={Text.color.light}>
                        <ul>
                          {metadata?.taxonomies.map((item) => {
                            return (
                              <Tooltip
                                title={
                                  <ul>
                                    <li>container version: {item.containerVersion}</li>
                                    <li>validation version: {item.validationVersion}</li>
                                    <li>package guid: {item.packageGuid}</li>
                                    <li>particles version: {item.particlesVersion}</li>
                                  </ul>
                                }
                              >
                                <li
                                  className="underline underline-offset-4 hover:text-primary-main"
                                  onClick={() => copyTaxonomyMetadata(item)}
                                >
                                  {item.name}
                                </li>
                              </Tooltip>
                            );
                          })}
                        </ul>
                      </Text>
                    </TableCell>
                    <TableCell align="right">
                      {metadata?.bftStatus === 'Failed' && (
                        <FileProcessingInfo
                          clientId={clientId}
                          reportId={reportId}
                          reportFileId={file.reportFileId}
                          context={metadata.bftLogContext}
                          severity="Error"
                        />
                      )}
                      <Chip
                        label={metadata?.bftStatus && BftStatusName[metadata.bftStatus]}
                        className={cx(
                          match(metadata?.bftStatus)
                            .with('NotYetStarted', () => 'bg-grey')
                            .with('Processing', () => 'bg-primary-blue')
                            .with('Done', () => 'bg-status-success')
                            .with('Failed', () => 'bg-status-error')
                            .otherwise(() => 'bg-grey'),
                          'text-white',
                        )}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {metadata?.visualExcelStatus === 'Failed' && (
                        <FileProcessingInfo
                          clientId={clientId}
                          reportId={reportId}
                          reportFileId={file.reportFileId}
                          context={metadata.visualExcelLogContext}
                          severity="Error"
                        />
                      )}

                      <Show when={metadata?.visualExcelStatus === 'Succeeded'}>
                        <IconButton
                          onClick={() => onDownloadExcel(VersionFileId.parse(file.id))}
                          size="small"
                          className="text-primary-main mr-1"
                        >
                          <FileDownloadOutlinedIcon />
                        </IconButton>
                      </Show>

                      <Chip
                        label={metadata?.visualExcelStatus && VisualExcelStatusName[metadata.visualExcelStatus]}
                        className={cx(
                          match(metadata?.visualExcelStatus)
                            .with('Pending', () => 'bg-grey')
                            .with('Succeeded', () => 'bg-status-success')
                            .with('Failed', () => 'bg-status-error')
                            .otherwise(() => 'bg-grey'),
                          'text-white',
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Dialog>
  );
};

export default UploadedFilesDialog;
